/* Hero: bestemmer hvilken utgave som kjøres, og kobler sammen delene.
 *
 * Markeringen fra Jinja2 er den statiske utgaven: posterbildet bak, og de fire
 * stegene som vanlig innhold. Den står som den er hvis noe av dette er sant:
 *
 *   - brukeren har bedt om redusert bevegelse
 *   - nettleseren har ikke WebGL
 *   - terrengfila eller scenen feiler av en eller annen grunn
 *
 * Ellers legges klassen .animert på seksjonen, scenen lastes i en egen fil, og
 * sekvensen tar over. Skifter brukeren «reduser bevegelse» underveis, avsluttes
 * scenen og den statiske utgaven kommer tilbake.
 */

import gsap from 'gsap';
import ScrollTrigger from 'gsap/ScrollTrigger';
import { $, mindreBevegelse } from '../kjerne/verktoy.js';

export function harWebGL() {
  try {
    const c = document.createElement('canvas');
    return Boolean(c.getContext('webgl2') || c.getContext('webgl'));
  } catch {
    return false;
  }
}

function introAnimasjon(hero) {
  if (mindreBevegelse.matches) return;
  gsap.timeline()
    .from(hero.querySelectorAll('h1 .tegn'), { yPercent: 130, rotate: 6, duration: 1.2, ease: 'expo.out', stagger: .018 })
    .from(hero.querySelectorAll('.toner-inn'), { y: 20, opacity: 0, duration: 1, ease: 'expo.out', stagger: .1 }, '-=.9')
    .from('.topplinje .rad > *', { y: -24, opacity: 0, duration: 1, ease: 'expo.out', stagger: .08 }, '<');
}

export function startHero() {
  const hero = $('.hero');
  if (!hero) return;

  const lerret = $('#fjord', hero);
  const poster = $('#hero-poster', hero);
  let aktiv = null;   // { scene, sekvens }

  /* ?poster=1 rendrer scenen alene, uten tekst og uten intro-bevegelse.
     bygg/poster.mjs bruker den til å lage posterbildet. */
  const postermodus = new URLSearchParams(location.search).has('poster');
  if (postermodus) document.body.classList.add('postermodus');

  if (!postermodus) introAnimasjon(hero);

  async function slaPaScenen() {
    if (aktiv || !harWebGL()) return;
    if (mindreBevegelse.matches && !postermodus) return;

    hero.classList.add('animert');
    ScrollTrigger.refresh();

    try {
      const [{ lagScene }, { lagSekvens }] = await Promise.all([
        import('./scene.js'),
        import('./sekvens.js'),
      ]);

      const scene = await lagScene({
        lerret,
        hero,
        rolig: () => mindreBevegelse.matches,
        vedForsteFrame: () => {
          // Nå har scenen noe å vise. Posteren blir liggende bak som reserve,
          // men skal ikke leses opp to ganger.
          lerret.style.opacity = '1';
          poster?.setAttribute('aria-hidden', 'true');
          if (postermodus) window.__elpPosterKlar = true;
        },
        vedKontekststap: () => {
          lerret.style.opacity = '0';
          poster?.removeAttribute('aria-hidden');
        },
      });

      const sekvens = lagSekvens({ hero, tilstand: scene.tilstand });
      scene.start();
      ScrollTrigger.refresh();

      aktiv = { scene, sekvens };
      // Til QA-skriptene: gjør nivå og ressursbruk lesbart utenfra.
      window.__elpHero = {
        nivaa: scene.nivaa.navn,
        terreng: scene.meta,
        ressurser: () => scene.ressurser(),
      };
    } catch (feil) {
      console.warn('Fjordscenen kunne ikke startes, viser posterbildet:', feil);
      slaAvScenen();
    }
  }

  function slaAvScenen() {
    aktiv?.sekvens.avslutt();
    aktiv?.scene.avslutt();
    aktiv = null;
    hero.classList.remove('animert');
    lerret.style.opacity = '0';
    poster?.removeAttribute('aria-hidden');
    ScrollTrigger.refresh();
  }

  slaPaScenen();

  /* Brukeren kan skru «reduser bevegelse» av og på midt i besøket. */
  mindreBevegelse.addEventListener('change', () => {
    if (mindreBevegelse.matches) slaAvScenen();
    else slaPaScenen();
  });
}
